import React, { useEffect, useState } from 'react';
import useSWR from 'swr';

import { DateTime } from 'luxon';
import { PageComponentProps } from '../../../../types/PageComponentProps';
import Table from '../../../../Common/Components/Table';
import AdminSidebar from '../../../../Common/Components/AdminSidebar';
import TableToolbar from '../Components/TableToolbar';
import Button from '../../../../Common/Components/Button';
import getHeaderProps from '../../../../utils/getHeaderProps';
import { DtFotaCampaign, DtFotaCampaignTableItem } from '../../../../types/FotaCampaigns';
import { ReactComponent as EditIcon } from '../../../../img/icons/edit.svg';
import { ReactComponent as StopIcon } from '../../../../img/icons/job-stopped-solid.svg';
import CreateEditDtFotaCampaignModal from './Components/CreateEditDtFotaCampaignModal';
import Modal from '../../../../Common/Components/Modal';
import { postRequest } from '../../../../utils/fetch';
import { useAppContext } from '../../../../utils/AppContext';
import DtFotaCampaignDetailsModal from './Components/DtFotaCampaignDetailsModal';
import AdminSidebarAdminItems from '../Components/AdminSidebarAdminItems';
import Utils from '../../../../utils/Utils';

const ACTIVE = 'active';
const CREATED = 'created';

const getStateDot = (state: string) => (
  <div>
    <span className={`status-dot ${state.toLowerCase()}`} />
    {state}
  </div>
);

function DtFotaCampaignsPage(props: PageComponentProps): JSX.Element {
  const { addNotification } = useAppContext();

  const modalCreateDtFotaCampaign = 1;
  const modalEditDtFotaCampaign = 2;
  const modalStopDtFotaCampaign = 3;
  const modalDtFotaCampaignDetails = 4;

  const headers = getHeaderProps('DtFotaCampaigns');
  const [openModal, setOpenModal] = useState(0);
  const [dtFotaCampaigns, setDtFotaCampaigns] = useState<DtFotaCampaignTableItem[]>([]);
  const [selectedDtFotaCampaign, setSelectedDtFotaCampaign] = useState<DtFotaCampaign>();

  const isNonReadOnly = Utils.isNonReadOnly();

  const { data: dtFotaCampaignsResp, mutate: updateDtFotaCampaignsResp } = useSWR<DtFotaCampaign[]>(
    ['/fota/campaign?campaignType=DT', 'GetDtFotaCampaigns'],
  );

  const colWidthCalcFn = (col: number, width: number): number => {
    const { actions, status } = { status: 130, actions: 150 };
    const remainColCount = col - 2;
    const colWidth = (width - status - actions) / (remainColCount);
    const notCustomizableHeadersWidth = [status, actions];
    const customizableHeadersWidth = Array.from(new Array(headers.length - notCustomizableHeadersWidth.length), (item) => item || colWidth);

    return [...notCustomizableHeadersWidth, ...customizableHeadersWidth][col];
  };

  useEffect(() => {
    if (dtFotaCampaignsResp && dtFotaCampaignsResp.length > 0) {
      setDtFotaCampaigns(dtFotaCampaignsResp.map((item) => {
        const state = item.state?.toLowerCase();
        const numberOfNodes = item.numberOfNodes || 0;
        const tableItem: DtFotaCampaignTableItem = {
          name: item.name,
          state: getStateDot(item.state || ''),
          stateTableFilter: item.state || '',
          stateTableSort: item.state || '',
          creationDate: (item.creationDate) ? DateTime.fromISO(item.creationDate).toISODate() : 'Data missing',
          targetVersion: item.targetVersion,
          firmwareType: item.firmwareType || '',
          numberOfNodes,
          numberOfNodesCompleted: item.numberOfNodesCompleted || 0,
          downloadRetryCount: item.downloadRetryCount,
          downloadHttpTimeout: `${item.downloadHttpTimeout} sec`,
          onDemand: item.onDemand === true ? 'True' : 'False',
          campaignTimeout: `${item.campaignTimeout} day${item.campaignTimeout > 1 ? 's' : ''}`,
          errorMessage: item.errorMessage || '',
          actions: (
            <div className="fota-campaign-actions">
              { isNonReadOnly && (
              <>
                <div className="fota-campaign-actions--item">
                  {(state === ACTIVE || state === CREATED) && (
                  <EditIcon
                    onClick={() => {
                      setSelectedDtFotaCampaign(item);
                      setOpenModal(modalEditDtFotaCampaign);
                    }}
                  />
                  )}
                </div>
                <div className="fota-campaign-actions--item">
                  {(state === ACTIVE || state === CREATED) && (
                  <StopIcon
                    onClick={() => {
                      setSelectedDtFotaCampaign(item);
                      setOpenModal(modalStopDtFotaCampaign);
                    }}
                  />
                  )}
                </div>
              </>
              )}
              <div className="fota-campaign-actions--item">
                {numberOfNodes > 0 && (
                  <Button
                    onClick={() => {
                      setSelectedDtFotaCampaign(item);
                      setOpenModal(modalDtFotaCampaignDetails);
                    }}
                    label="Details"
                    buttonType="secondary"
                    buttonSize="small"
                  />
                )}
              </div>
            </div>
          ),
        };

        return tableItem;
      }) // eslint-disable-next-line no-nested-ternary
        .sort((a, b) => ((a.creationDate > b.creationDate) ? -1 : 1)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dtFotaCampaignsResp]);

  return (
    <div className="config__content fota-campaigns">
      <AdminSidebar title="Account admin">
        <AdminSidebarAdminItems path={window.location.pathname} />
      </AdminSidebar>
      <div className="page-content page-content--medium-margin">
        <TableToolbar title="DTFOTA Campaigns">
          <div className="fota-campaigns-tabletoolbar">
            { isNonReadOnly && (
            <div className="tabletoolbar__children">
              <Button
                label="Create new"
                buttonType="secondary"
                onClick={() => setOpenModal(modalCreateDtFotaCampaign)}
              />
            </div>
            )}
          </div>
        </TableToolbar>
        <div className="table table--light top-border-light auto-height">
          <Table
            headers={headers}
            data={dtFotaCampaigns}
            autoheight
            colWidthCalcFn={colWidthCalcFn}
          />
        </div>
      </div>
      {openModal === modalCreateDtFotaCampaign && (
        <CreateEditDtFotaCampaignModal
          setOpenModal={setOpenModal}
          updateDtFotaCampaignsResp={updateDtFotaCampaignsResp}
        />
      )}
      {selectedDtFotaCampaign !== undefined && (
        (openModal === modalEditDtFotaCampaign && (
          <CreateEditDtFotaCampaignModal
            campaign={selectedDtFotaCampaign}
            setOpenModal={setOpenModal}
            updateDtFotaCampaignsResp={updateDtFotaCampaignsResp}
          />
        ))
        || (openModal === modalStopDtFotaCampaign && (
          <Modal
            title="Stop DTFOTA campaign"
            width="360"
            height="220"
            className="stop-dt-fota-campaign"
            setModalOpen={() => setOpenModal(0)}
            primaryButtonLabel="Confirm"
            primaryButtonAction={async () => {
              try {
                const result = await postRequest(`/fota/campaign/${selectedDtFotaCampaign.campaignId}/app/close`);

                if (!result.error) {
                  addNotification({ type: 'info', message: 'Your "Stop DTFOTA campaign" operation is completed.' });
                  setOpenModal(0);
                  updateDtFotaCampaignsResp();
                } else {
                  addNotification({ type: 'error', message: `Your "Stop DTFOTA campaign" operation has failed: ${result.error}` });
                  setOpenModal(0);
                }
              } catch (e) {
                addNotification({ type: 'error', message: 'Your "Stop DTFOTA campaign" operation has failed.' });
                setOpenModal(0);
              }
            }}
            secondaryButtonLabel="Cancel"
          >
            <span>Are you sure you want to stop this DTFOTA campaign?</span>
          </Modal>
        ))
        || (openModal === modalDtFotaCampaignDetails && (
          <DtFotaCampaignDetailsModal
            campaign={selectedDtFotaCampaign}
            setOpenModal={setOpenModal}
            getStateDot={getStateDot}
          />
        ))
      )}
    </div>
  );
}

export default DtFotaCampaignsPage;
